import React from "react"
import { SignedIn, SignedOut } from "@clerk/clerk-react"
import { Button } from "@/components/ui/button"
import { ArrowRight, Calendar, DollarSign, Users } from "lucide-react"
import { MockSignedIn, MockSignedOut } from "@/lib/clerk-mock"

// Check if we're in E2E mode
const isE2EMode = import.meta.env.VITE_E2E_MODE === 'true';

// Use mocked components in E2E mode
const AuthSignedIn = isE2EMode ? MockSignedIn : SignedIn;
const AuthSignedOut = isE2EMode ? MockSignedOut : SignedOut;

// Analytics tracking function
const trackCTAClick = (ctaType: string, action: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'cta_click', {
      event_category: 'Conversion',
      event_label: ctaType,
      cta_action: action,
      non_interaction: false,
    });
  }
}

export default function CtaSection() {
  const handlePrimaryClick = () => {
    trackCTAClick('primary', 'sign_up');
    window.location.href = "/pa/sign-up";
  };

  const handlePricingClick = () => {
    trackCTAClick('secondary', 'view_pricing');
    // Scroll to pricing section or navigate to pricing page
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDemoClick = () => {
    trackCTAClick('tertiary', 'book_demo');
    // For now, redirect to contact - can be updated with actual demo booking
    window.location.href = "/contact";
  };

  const handleDashboardClick = () => {
    trackCTAClick('authenticated', 'dashboard');
    window.location.href = "/pa/dashboard";
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative z-10 text-center">
      <div className="container mx-auto px-4">
        {/* Main Heading */}
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
          Ready to Reclaim Your Time?
        </h2>

        {/* Social Proof */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Users className="h-5 w-5 text-brand-teal" aria-hidden="true" />
          <p className="text-lg text-gray-300">
            Join <span className="font-semibold text-brand-teal">10,000+</span> users saving 2+ hours daily
          </p>
        </div>

        {/* CTA Buttons for Signed Out Users */}
        <AuthSignedOut>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            {/* Primary CTA */}
            <Button
              size="lg"
              onClick={handlePrimaryClick}
              className="bg-brand-teal hover:bg-brand-teal/90 text-slate-900 font-semibold px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-200 group"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Button>

            {/* Secondary CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="outline"
                onClick={handlePricingClick}
                className="border-gray-600 text-white hover:bg-gray-800 hover:text-white px-6 py-6 text-base font-medium"
              >
                <DollarSign className="mr-2 h-4 w-4" aria-hidden="true" />
                View Pricing
              </Button>

              <Button
                size="lg"
                variant="ghost"
                onClick={handleDemoClick}
                className="text-gray-300 hover:text-white hover:bg-gray-800 px-6 py-6 text-base font-medium"
              >
                <Calendar className="mr-2 h-4 w-4" aria-hidden="true" />
                Book Demo
              </Button>
            </div>
          </div>

          {/* Risk Reduction Elements */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              ✓ No credit card required
            </span>
            <span className="flex items-center gap-1">
              ✓ Cancel anytime
            </span>
            <span className="flex items-center gap-1">
              ✓ 30-day money-back guarantee
            </span>
          </div>
        </AuthSignedOut>

        {/* CTA for Signed In Users */}
        <AuthSignedIn>
          <Button
            size="lg"
            onClick={handleDashboardClick}
            className="bg-brand-teal hover:bg-brand-teal/90 text-slate-900 font-semibold px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-200 group"
          >
            Go to Dashboard
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </Button>
        </AuthSignedIn>
      </div>
    </section>
  )
}
